/**
 * Cycle Comparison Set Store
 * 
 * Manages criteria selection for cycles, including locking criteria
 * for the GATHER→ARRANGE gate.
 * 
 * Per the Authoritative Cycles implementation plan:
 * - Criteria must be locked before advancing to ARRANGE phase
 * - Lock status is stored in cycle_comparison_sets.locked_at
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';
import { checkPolicyForCycle } from '../../lib/authorization/storeEnforcement';
import { useDegapProcessStore } from './degapProcessStore';
import {
  resolveWorkflowCriteriaGovernance,
} from '../../lib/workflowCriteriaGovernance';

// ============================================================================
// Types
// ============================================================================

export interface CycleComparisonSet {
  id: string;
  cycleId: string;
  name?: string;
  itemIds: string[];           // Items in the candidate pool
  criteriaIds: string[];       // Criteria (attributes) for comparison
  criteriaWeights: Record<string, number>;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  lockedAt?: Date;
  lockedBy?: string;
  
  // Legacy field mappings for backward compatibility
  attributeIds: string[];      // Alias for criteriaIds
  weights: Record<string, number>; // Alias for criteriaWeights
}

interface CycleComparisonSetState {
  sets: CycleComparisonSet[];
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;

  // CRUD
  fetchForCycle: (cycleId: string) => Promise<void>;
  createSet: (params: {
    cycleId: string;
    name?: string;
    attributeIds: string[];
    weights?: Record<string, number>;
    createdBy: string;
  }) => Promise<CycleComparisonSet | null>;
  updateSet: (id: string, updates: Partial<Pick<CycleComparisonSet, 'name' | 'attributeIds' | 'weights'>>) => Promise<boolean>;
  
  // Criteria management with governance events
  updateCriteria: (cycleId: string, criteriaIds: string[], weights: Record<string, number>, userId: string) => Promise<boolean>;
  
  // Locking
  lockCriteria: (cycleId: string, userId: string) => Promise<boolean>;
  unlockCriteria: (cycleId: string, userId: string) => Promise<boolean>;
  
  // Candidate pool sync
  syncItemsFromCycle: (cycleId: string, itemIds: string[], userId: string) => Promise<boolean>;
  
  // Selectors
  getSetForCycle: (cycleId: string) => CycleComparisonSet | undefined;
  isCriteriaLocked: (cycleId: string) => boolean;
}

// ============================================================================
// Database Helpers
// ============================================================================

function dbToSet(row: Record<string, unknown>): CycleComparisonSet {
  const criteriaIds = (row.criteria_ids as string[]) || [];
  const criteriaWeights = (row.criteria_weights as Record<string, number>) || {};
  
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    name: row.name as string | undefined,
    itemIds: (row.item_ids as string[]) || [],
    criteriaIds,
    criteriaWeights,
    createdBy: row.created_by as string,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
    lockedAt: row.locked_at ? new Date(row.locked_at as string) : undefined,
    lockedBy: row.locked_by as string | undefined,
    
    // Legacy aliases
    attributeIds: criteriaIds,
    weights: criteriaWeights,
  };
}

// ============================================================================
// Store
// ============================================================================

export const useCycleComparisonSetStore = create<CycleComparisonSetState>()((set, get) => ({
  sets: [],
  isLoading: false,
  isCreating: false,
  error: null,

  fetchForCycle: async (cycleId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('cycle_comparison_sets')
        .select('*')
        .eq('cycle_id', cycleId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[CycleComparisonSetStore] Fetch error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }

      const fetched = (data || []).map(dbToSet);
      
      set(state => {
        const other = state.sets.filter(s => s.cycleId !== cycleId);
        return {
          sets: [...other, ...fetched],
          isLoading: false,
          error: null,
        };
      });

      console.log(`[CycleComparisonSetStore] Fetched ${fetched.length} sets for cycle ${cycleId}`);
    } catch (err) {
      console.error('[CycleComparisonSetStore] Fetch exception:', err);
      set({ isLoading: false, error: String(err) });
    }
  },

  createSet: async (params) => {
    const cycle = useDegapProcessStore.getState().getCycle(params.cycleId);
    if (!cycle) {
      set({ error: 'Cycle not found' });
      return null;
    }
    const policyDecision = await checkPolicyForCycle('process.edit', params.cycleId, cycle as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[CycleComparisonSetStore] Policy denied for process.edit (createSet):', reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'cycleComparisonSet', action: 'createSet', message: `Cannot create set: ${reasonCode}`, decision: policyDecision },
        }));
      }
      set({ error: reasonCode });
      return null;
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    set({ isCreating: true, error: null });

    const now = new Date();
    const newSet = {
      id: generateUUID(),
      cycle_id: params.cycleId,
      name: params.name,
      criteria_ids: params.attributeIds,
      criteria_weights: params.weights || {},
      created_by: params.createdBy,
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
    };

    try {
      const { data, error } = await supabase
        .from('cycle_comparison_sets')
        .insert(newSet)
        .select()
        .single();

      if (error) {
        console.error('[CycleComparisonSetStore] Create error:', error.message, error.code, error.details);
        set({ isCreating: false, error: error.message });
        return null;
      }

      const created = dbToSet(data);
      set(state => ({ sets: [...state.sets, created], isCreating: false, error: null }));
      
      console.log('[CycleComparisonSetStore] Created set:', created.id);
      return created;
    } catch (err) {
      console.error('[CycleComparisonSetStore] Create exception:', err);
      set({ isCreating: false, error: String(err) });
      return null;
    }
  },

  updateSet: async (id, updates) => {
    const existingSet = get().sets.find(s => s.id === id);
    if (!existingSet) return false;
    const cycle = useDegapProcessStore.getState().getCycle(existingSet.cycleId);
    if (!cycle) return false;
    const policyDecision = await checkPolicyForCycle('process.edit', existingSet.cycleId, cycle as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[CycleComparisonSetStore] Policy denied for process.edit (updateSet):', reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'cycleComparisonSet', action: 'updateSet', message: `Cannot update set: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return false;
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const dbUpdates: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.attributeIds !== undefined) dbUpdates.criteria_ids = updates.attributeIds;
    if (updates.weights !== undefined) dbUpdates.criteria_weights = updates.weights;

    try {
      const { error } = await supabase
        .from('cycle_comparison_sets')
        .update(dbUpdates)
        .eq('id', id);

      if (error) {
        console.error('[CycleComparisonSetStore] Update error:', error);
        return false;
      }

      set(state => ({
        sets: state.sets.map(s =>
          s.id === id ? { ...s, ...updates, updatedAt: new Date() } : s
        ),
      }));

      return true;
    } catch (err) {
      console.error('[CycleComparisonSetStore] Update exception:', err);
      return false;
    }
  },

  /**
   * Update criteria for a cycle with governance event
   * Per Build Contract: criteria selection is a persisted, auditable action
   */
  updateCriteria: async (cycleId: string, criteriaIds: string[], weights: Record<string, number>, userId: string) => {
    const cycle = useDegapProcessStore.getState().getCycle(cycleId);
    if (!cycle) return false;

    // Workflow governance: check that no locked required attributes are being dropped.
    // criteriaIds are attribute UUIDs; we look up their keys to compare against locked ones.
    const governance = await resolveWorkflowCriteriaGovernance(cycleId);
    if (governance && governance.mode === 'workflow_defined' && (governance.lockedAttributes ?? []).length > 0) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { data: attrRows } = await supabase
          .from('attributes')
          .select('id, attribute_key')
          .in('id', criteriaIds);

        const newKeySet = new Set((attrRows ?? []).map((r: { attribute_key: string }) => r.attribute_key));

        for (const locked of governance.lockedAttributes ?? []) {
          if (!locked.required) continue;
          if (!newKeySet.has(locked.attributeKey)) {
            const msg = `Criterion "${locked.attributeKey}" is required by the workflow and cannot be removed.`;
            console.warn('[CycleComparisonSetStore] Workflow governance blocked updateCriteria:', msg);
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('authorization-denied', {
                detail: { store: 'cycleComparisonSet', action: 'updateCriteria', message: msg, code: 'CRITERIA_WORKFLOW_LOCKED' },
              }));
            }
            return false;
          }
        }
      }
    }

    const policyDecision = await checkPolicyForCycle('criteria.edit', cycleId, cycle as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[CycleComparisonSetStore] Policy denied for criteria.edit (updateCriteria):', reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'cycleComparisonSet', action: 'updateCriteria', message: `Cannot update criteria: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return false;
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const existingSet = get().getSetForCycle(cycleId);
    const now = new Date();

    try {
      if (!existingSet) {
        // Create a new comparison set with the criteria
        const newSet = {
          id: generateUUID(),
          cycle_id: cycleId,
          item_ids: [],
          criteria_ids: criteriaIds,
          criteria_weights: weights,
          created_by: userId,
          created_at: now.toISOString(),
          updated_at: now.toISOString(),
        };

        const { data, error } = await supabase
          .from('cycle_comparison_sets')
          .insert(newSet)
          .select()
          .single();

        if (error) {
          console.error('[CycleComparisonSetStore] Create error during criteria update:', error.message, error.code, error.details);
          return false;
        }

        const created = dbToSet(data);
        set(state => ({ sets: [...state.sets, created] }));
        console.log(`[CycleComparisonSetStore] Created set with ${criteriaIds.length} criteria for cycle ${cycleId}`);
      } else {
        // Update existing set with new criteria
        const { error } = await supabase
          .from('cycle_comparison_sets')
          .update({
            criteria_ids: criteriaIds,
            criteria_weights: weights,
            updated_at: now.toISOString(),
          })
          .eq('id', existingSet.id);

        if (error) {
          console.error('[CycleComparisonSetStore] Update error during criteria update:', error.message, error.code, error.details);
          return false;
        }

        set(state => ({
          sets: state.sets.map(s =>
            s.id === existingSet!.id
              ? { ...s, criteriaIds, attributeIds: criteriaIds, criteriaWeights: weights, weights, updatedAt: now }
              : s
          ),
        }));
        console.log(`[CycleComparisonSetStore] Updated criteria to ${criteriaIds.length} for cycle ${cycleId}`);
      }

      // Record governance event (non-blocking)
      supabase.from('governance_events').insert({
        id: generateUUID(),
        cycle_id: cycleId,
        event_type: 'criteria_updated',
        actor_id: userId,
        occurred_at: now.toISOString(),
        note: JSON.stringify({ criteriaCount: criteriaIds.length, criteriaIds }),
      }).then(({ error }) => {
        if (error) {
          console.warn('[CycleComparisonSetStore] Governance event failed (non-blocking):', error.message);
        }
      });

      return true;
    } catch (err) {
      console.error('[CycleComparisonSetStore] UpdateCriteria exception:', err);
      return false;
    }
  },

  lockCriteria: async (cycleId, userId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const existingSet = get().getSetForCycle(cycleId);
    if (!existingSet) {
      console.error('[CycleComparisonSetStore] No comparison set to lock for cycle:', cycleId);
      return false;
    }

    const cycle = useDegapProcessStore.getState().getCycle(cycleId);
    if (!cycle) {
      console.error('[CycleComparisonSetStore] Cycle not found:', cycleId);
      return false;
    }
    const policyDecision = await checkPolicyForCycle('criteria.edit', cycleId, cycle as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[CycleComparisonSetStore] Policy denied for criteria.edit (lockCriteria):', reasonCode);
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: { store: 'cycleComparisonSet', action: 'lockCriteria', message: `Cannot lock criteria: ${reasonCode}`, decision: policyDecision },
      }));
      return false;
    }

    const now = new Date();

    try {
      const { error } = await supabase
        .from('cycle_comparison_sets')
        .update({
          locked_at: now.toISOString(),
          locked_by: userId,
          updated_at: now.toISOString(),
        })
        .eq('id', existingSet.id);

      if (error) {
        console.error('[CycleComparisonSetStore] Lock error:', error);
        return false;
      }

      set(state => ({
        sets: state.sets.map(s =>
          s.id === existingSet.id
            ? { ...s, lockedAt: now, lockedBy: userId, updatedAt: now }
            : s
        ),
      }));

      // Record governance event
      await supabase.from('governance_events').insert({
        id: generateUUID(),
        cycle_id: cycleId,
        event_type: 'criteria_locked',
        actor_id: userId,
        occurred_at: now.toISOString(),
        note: JSON.stringify({ setId: existingSet.id, attributeCount: existingSet.attributeIds.length }),
      });

      console.log(`[CycleComparisonSetStore] Criteria locked for cycle ${cycleId}`);
      return true;
    } catch (err) {
      console.error('[CycleComparisonSetStore] Lock exception:', err);
      return false;
    }
  },

  unlockCriteria: async (cycleId, userId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const existingSet = get().getSetForCycle(cycleId);
    if (!existingSet) return false;

    const cycle = useDegapProcessStore.getState().getCycle(cycleId);
    if (!cycle) return false;
    const policyDecision = await checkPolicyForCycle('criteria.edit', cycleId, cycle as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[CycleComparisonSetStore] Policy denied for criteria.edit (unlockCriteria):', reasonCode);
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: { store: 'cycleComparisonSet', action: 'unlockCriteria', message: `Cannot unlock criteria: ${reasonCode}`, decision: policyDecision },
      }));
      return false;
    }

    try {
      const { error } = await supabase
        .from('cycle_comparison_sets')
        .update({
          locked_at: null,
          locked_by: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingSet.id);

      if (error) {
        console.error('[CycleComparisonSetStore] Unlock error:', error);
        return false;
      }

      set(state => ({
        sets: state.sets.map(s =>
          s.id === existingSet.id
            ? { ...s, lockedAt: undefined, lockedBy: undefined, updatedAt: new Date() }
            : s
        ),
      }));

      // Record governance event
      await supabase.from('governance_events').insert({
        id: generateUUID(),
        cycle_id: cycleId,
        event_type: 'criteria_unlocked',
        actor_id: userId,
        occurred_at: new Date().toISOString(),
      });

      console.log(`[CycleComparisonSetStore] Criteria unlocked for cycle ${cycleId}`);
      return true;
    } catch (err) {
      console.error('[CycleComparisonSetStore] Unlock exception:', err);
      return false;
    }
  },

  /**
   * Sync item IDs from cycle to comparison set
   * Per Build Contract: candidate pool selection is a persisted state transition
   */
  syncItemsFromCycle: async (cycleId, itemIds, userId) => {
    const cycle = useDegapProcessStore.getState().getCycle(cycleId);
    if (!cycle) return false;
    const policyDecision = await checkPolicyForCycle('process.edit', cycleId, cycle as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[CycleComparisonSetStore] Policy denied for process.edit (syncItemsFromCycle):', reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'cycleComparisonSet', action: 'syncItemsFromCycle', message: `Cannot sync items: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return false;
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const existingSet = get().getSetForCycle(cycleId);
    const now = new Date();

    try {
      if (!existingSet) {
        // Create a new comparison set with the items
        const newSet = {
          id: generateUUID(),
          cycle_id: cycleId,
          item_ids: itemIds,
          criteria_ids: [],
          criteria_weights: {},
          created_by: userId,
          created_at: now.toISOString(),
          updated_at: now.toISOString(),
        };

        const { data, error } = await supabase
          .from('cycle_comparison_sets')
          .insert(newSet)
          .select()
          .single();

        if (error) {
          console.error('[CycleComparisonSetStore] Create error during sync:', error);
          return false;
        }

        const created = dbToSet(data);
        set(state => ({ sets: [...state.sets, created] }));
        console.log(`[CycleComparisonSetStore] Created set with ${itemIds.length} items for cycle ${cycleId}`);
      } else {
        // Update existing set with new items
        const { error } = await supabase
          .from('cycle_comparison_sets')
          .update({
            item_ids: itemIds,
            updated_at: now.toISOString(),
          })
          .eq('id', existingSet.id);

        if (error) {
          console.error('[CycleComparisonSetStore] Update error during sync:', error);
          return false;
        }

        set(state => ({
          sets: state.sets.map(s =>
            s.id === existingSet!.id
              ? { ...s, itemIds, updatedAt: now }
              : s
          ),
        }));
        console.log(`[CycleComparisonSetStore] Updated set with ${itemIds.length} items for cycle ${cycleId}`);
      }

      // Record governance event (non-blocking)
      supabase.from('governance_events').insert({
        id: generateUUID(),
        cycle_id: cycleId,
        event_type: 'candidate_pool_updated',
        actor_id: userId,
        occurred_at: now.toISOString(),
        note: JSON.stringify({ itemCount: itemIds.length }),
      }).then(({ error }) => {
        if (error) {
          console.warn('[CycleComparisonSetStore] Governance event failed (non-blocking):', error.message);
        }
      });

      return true;
    } catch (err) {
      console.error('[CycleComparisonSetStore] Sync exception:', err);
      return false;
    }
  },

  // Selectors
  getSetForCycle: (cycleId) => {
    return get().sets.find(s => s.cycleId === cycleId);
  },

  isCriteriaLocked: (cycleId) => {
    const set = get().sets.find(s => s.cycleId === cycleId);
    return set?.lockedAt !== undefined;
  },
}));
